import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, Typography, Tooltip, Skeleton
} from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ResizeHandle from './ResizeHandle';
import ColumnFilter from './ColumnFilter';
import SortChipBar from './SortChipBar';
import useClientSidePagination from '../../hooks/useClientSidePagination';

/**
 * AdminTable — Climbrs-style table with column resize, multi-sort, per-column filters, pagination.
 *
 * Column config:
 * {
 *   field: string,           // row key
 *   headerName: string,      // display label
 *   width: number,           // default width in px
 *   minWidth?: number,       // minimum resize width (default 80)
 *   type?: string,           // 'text'|'numeric'|'date'|'singleSelect'|'multiselect'|'search' (controls filter UI)
 *   filterOptions?: array,   // for singleSelect/multiselect: [{value,label}] or string[]
 *   sortable?: boolean,      // default true
 *   filterable?: boolean,    // default true
 *   renderCell?: (row) => node, // custom cell renderer
 *   valueFormatter?: ({value}) => string, // simple value format
 *   valueGetter?: ({row}) => any, // compute value from row
 *   sticky?: boolean,        // stick column to left
 *   align?: string,          // 'left'|'center'|'right'
 * }
 */
export default function AdminTable({
  columns = [],
  rows = [],
  pageKey = 'admin_table',
  defaultSort = [],
  defaultPageSize = 25,
  loading = false,
  density = 'compact',
  getRowId = (row) => row.id,
  onRowClick,
  emptyMessage = 'No data',
}) {
  // --- State ---
  const [sortColumns, setSortColumns] = useState(
    defaultSort.map(s => ({ field: s.field, direction: s.sort || s.direction || 'asc' }))
  );
  const [filters, setFilters] = useState({});
  const [columnWidths, setColumnWidths] = useState(() => {
    const widths = {};
    columns.forEach(c => { widths[c.field] = c.width || 150; });
    return widths;
  });
  const [resizingColumn, setResizingColumn] = useState(null);
  const resizeRef = useRef({ startX: 0, startWidth: 0, field: null });

  // Update widths when columns change
  useEffect(() => {
    setColumnWidths(prev => {
      const next = { ...prev };
      columns.forEach(c => { if (!(c.field in next)) next[c.field] = c.width || 150; });
      return next;
    });
  }, [columns]);

  // --- Filtering ---
  const setFilter = useCallback((field, value) => {
    setFilters(prev => {
      const next = { ...prev };
      if (value === null || value === undefined || value === 'all' || (Array.isArray(value) && value.length === 0)) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
  }, []);

  const filteredRows = useMemo(() => {
    if (Object.keys(filters).length === 0) return rows;
    return rows.filter(row => {
      return Object.entries(filters).every(([field, filterVal]) => {
        const col = columns.find(c => c.field === field);
        const cellVal = col?.valueGetter ? col.valueGetter({ row }) : row[field];

        // Multiselect array
        if (Array.isArray(filterVal)) {
          return filterVal.includes(cellVal) || filterVal.includes(String(cellVal));
        }
        // Numeric filter object
        if (typeof filterVal === 'object' && filterVal.operator) {
          const op = filterVal.operator;
          const fv = filterVal.value;
          if (op === 'isNull') return cellVal === null || cellVal === undefined || cellVal === '';
          if (op === 'isNotNull') return cellVal !== null && cellVal !== undefined && cellVal !== '';
          if (op === 'isEmpty') return !cellVal;
          if (op === 'isNotEmpty') return !!cellVal;
          const numVal = typeof cellVal === 'number' ? cellVal : parseFloat(cellVal);
          const numFilter = typeof fv === 'number' ? fv : parseFloat(fv);
          if (isNaN(numVal)) return false;
          // Date operators
          if (op === 'equals') {
            if (col?.type === 'date') return String(cellVal).startsWith(fv);
            return numVal === numFilter;
          }
          if (op === 'greater' || op === 'after') return col?.type === 'date' ? String(cellVal) > fv : numVal > numFilter;
          if (op === 'less' || op === 'before') return col?.type === 'date' ? String(cellVal) < fv : numVal < numFilter;
          if (op === 'greaterEqual' || op === 'afterEqual') return col?.type === 'date' ? String(cellVal) >= fv : numVal >= numFilter;
          if (op === 'lessEqual' || op === 'beforeEqual') return col?.type === 'date' ? String(cellVal) <= fv : numVal <= numFilter;
          if (op === 'notEqual') return numVal !== numFilter;
          // Text operators
          const sv = String(cellVal || '').toLowerCase();
          const tv = String(fv || '').toLowerCase();
          if (op === 'contains') return sv.includes(tv);
          if (op === 'startsWith') return sv.startsWith(tv);
          if (op === 'endsWith') return sv.endsWith(tv);
          if (op === 'notContains') return !sv.includes(tv);
          return true;
        }
        // Simple string match
        if (typeof filterVal === 'string') {
          return String(cellVal || '').toLowerCase().includes(filterVal.toLowerCase());
        }
        return true;
      });
    });
  }, [rows, filters, columns]);

  // --- Sorting ---
  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      for (const { field, direction } of sortColumns) {
        const col = columns.find(c => c.field === field);
        const av = col?.valueGetter ? col.valueGetter({ row: a }) : a[field];
        const bv = col?.valueGetter ? col.valueGetter({ row: b }) : b[field];
        let cmp = 0;
        if (av == null && bv == null) cmp = 0;
        else if (av == null) cmp = 1;
        else if (bv == null) cmp = -1;
        else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [filteredRows, sortColumns, columns]);

  // --- Pagination ---
  const { paginatedData, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useClientSidePagination(sortedRows, { initialRowsPerPage: defaultPageSize });

  // --- Column resize ---
  const handleResizeStart = useCallback((field, e) => {
    e.preventDefault();
    e.stopPropagation();
    setResizingColumn(field);
    resizeRef.current = { startX: e.clientX, startWidth: columnWidths[field] || 150, field };

    const handleMouseMove = (moveE) => {
      const diff = moveE.clientX - resizeRef.current.startX;
      const col = columns.find(c => c.field === resizeRef.current.field);
      const minW = col?.minWidth || 80;
      setColumnWidths(prev => ({ ...prev, [resizeRef.current.field]: Math.max(minW, resizeRef.current.startWidth + diff) }));
    };
    const handleMouseUp = () => {
      setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [columnWidths, columns]);

  // --- Sort click ---
  const handleSort = useCallback((field) => {
    setSortColumns(prev => {
      const idx = prev.findIndex(s => s.field === field);
      if (idx >= 0) {
        if (prev[idx].direction === 'asc') {
          const next = [...prev]; next[idx] = { field, direction: 'desc' }; return next;
        }
        return prev.filter((_, i) => i !== idx);
      }
      return [...prev, { field, direction: 'asc' }];
    });
  }, []);

  const getSortIcon = (field) => {
    const sortCol = sortColumns.find(s => s.field === field);
    const sortIdx = sortColumns.findIndex(s => s.field === field);
    if (!sortCol) return <UnfoldMoreIcon sx={{ ml: 0.5, fontSize: 18, opacity: 0.4, color: 'text.secondary' }} />;
    const showPriority = sortColumns.length > 1;
    const Icon = sortCol.direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
        {showPriority && <Typography component="span" sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1A2B4A', mr: 0.25 }}>{sortIdx + 1}</Typography>}
        <Icon sx={{ fontSize: 18, color: 'text.primary' }} />
      </Box>
    );
  };

  // --- Filter options builder ---
  const getFilterOptions = useCallback((col) => {
    if (col.filterOptions) {
      return col.filterOptions.map(o => typeof o === 'string' ? { value: o, label: o } : o);
    }
    if (col.type === 'singleSelect' || col.type === 'multiselect') {
      const unique = [...new Set(rows.map(r => r[col.field]).filter(v => v != null))].sort();
      return unique.map(v => ({ value: v, label: String(v) }));
    }
    return [];
  }, [rows]);

  const getFilterType = (col) => {
    if (col.type === 'singleSelect') return 'multiselect';
    if (col.type === 'multiselect') return 'multiselect';
    if (col.type === 'number' || col.type === 'numeric') return 'numeric';
    if (col.type === 'date') return 'date';
    if (col.type === 'text') return 'text';
    if (col.type === 'search') return 'search';
    return 'text';
  };

  // --- Sortable fields for SortChipBar ---
  const sortableFields = useMemo(() =>
    columns.filter(c => c.sortable !== false).map(c => ({ value: c.field, label: c.headerName })),
    [columns]
  );

  // --- Sticky column offset calculation ---
  const stickyOffsets = useMemo(() => {
    const offsets = {};
    let left = 0;
    columns.forEach(col => {
      if (col.sticky) {
        offsets[col.field] = left;
        left += (columnWidths[col.field] || col.width || 150);
      }
    });
    return offsets;
  }, [columns, columnWidths]);

  const cellPadding = density === 'compact' ? '6px 10px' : '12px 16px';
  const rowHeight = density === 'compact' ? 40 : 52;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sort chip bar */}
      <SortChipBar sortColumns={sortColumns} onSortChange={setSortColumns} sortableFields={sortableFields} />

      {/* Pagination top */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          {filteredRows.length !== rows.length ? `${filteredRows.length} of ${rows.length}` : `${rows.length}`} rows
        </Typography>
        <TablePagination
          component="div" count={sortedRows.length} page={page} rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ flexShrink: 0, borderBottom: 'none', '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          // 999.2131: skeleton rows per brand Loading & Busy-State Standard —
          // spinner-in-table deprecated; rows mirror the loaded geometry.
          <Box aria-busy="true" sx={{ p: 1 }}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.75, borderRadius: '2px' }} data-testid="admin-table-skeleton-row" />
            ))}
            <Box role="status" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
              Loading table…
            </Box>
          </Box>
        ) : (
          <Table stickyHeader size={density === 'compact' ? 'small' : 'medium'} sx={{ minWidth: '100%' }}>
            <TableHead>
              <TableRow>
                {columns.map(col => {
                  const w = columnWidths[col.field] || col.width || 150;
                  const isSticky = col.sticky;
                  const stickyLeft = isSticky ? stickyOffsets[col.field] : undefined;
                  return (
                    <TableCell
                      key={col.field}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        backgroundColor: '#F5F0E8',
                        color: '#5C5A55',
                        width: w, minWidth: w, maxWidth: w,
                        padding: cellPadding,
                        borderBottom: '2px solid #E8E0D0',
                        position: isSticky ? 'sticky' : undefined,
                        left: stickyLeft,
                        zIndex: isSticky ? 1002 : undefined,
                        '&:hover': { backgroundColor: '#EDE8DD' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                        {col.sortable !== false ? (
                          <Tooltip title={`Sort by ${col.headerName}`}>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1,
                                padding: '2px 4px', borderRadius: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' } }}
                              onClick={() => handleSort(col.field)}
                            >
                              <Typography variant="body2" sx={{
                                fontWeight: sortColumns.some(s => s.field === col.field) ? 700 : 600,
                                color: sortColumns.some(s => s.field === col.field) ? '#1A2B4A' : 'inherit',
                                fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em'
                              }}>
                                {col.headerName}
                              </Typography>
                              {getSortIcon(col.field)}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {col.headerName}
                          </Typography>
                        )}

                        {col.filterable !== false && (
                          <Box onClick={(e) => e.stopPropagation()}>
                            <ColumnFilter
                              type={getFilterType(col)}
                              options={getFilterOptions(col)}
                              value={filters[col.field]}
                              onChange={(val) => setFilter(col.field, val)}
                              placeholder={`Filter ${col.headerName}...`}
                            />
                          </Box>
                        )}

                        <ResizeHandle onMouseDown={(e) => handleResizeStart(col.field, e)} isResizing={resizingColumn === col.field} />
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => {
                  const rowId = getRowId(row);
                  return (
                    <TableRow
                      key={rowId}
                      hover
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        height: rowHeight,
                        '&:hover': { backgroundColor: '#FAF7F2' }
                      }}
                    >
                      {columns.map(col => {
                        const w = columnWidths[col.field] || col.width || 150;
                        const isSticky = col.sticky;
                        const stickyLeft = isSticky ? stickyOffsets[col.field] : undefined;
                        const rawVal = col.valueGetter ? col.valueGetter({ row }) : row[col.field];
                        let content;
                        if (col.renderCell) {
                          content = col.renderCell(row);
                        } else if (col.valueFormatter) {
                          content = col.valueFormatter({ value: rawVal, row });
                        } else {
                          content = rawVal != null ? String(rawVal) : '';
                        }
                        return (
                          <TableCell
                            key={col.field}
                            align={col.align || 'left'}
                            sx={{
                              width: w, minWidth: w, maxWidth: w,
                              padding: cellPadding,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontSize: '0.8rem',
                              position: isSticky ? 'sticky' : undefined,
                              left: stickyLeft,
                              zIndex: isSticky ? 1000 : undefined,
                              backgroundColor: isSticky ? 'background.paper' : undefined,
                            }}
                          >
                            {content}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
